// oxlint-disable complexity
import CloseIcon from '@mui/icons-material/Close'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Card,
  CardContent
} from '@mui/material'
import {LoadingButton} from '@mui/lab'
import {t} from 'i18next'
import GoogleMapReact from 'google-map-react'

import {PhoneNumberTextField} from '@hconnect/uikit'
import {useSelector} from 'react-redux'
import {AppState} from '../../Root.store'
import {getUserProfile} from '../../UserProfile/UserProfile.selectors'
import {User} from '@hconnect/apiclient'
import {CountryData} from 'intl-tel-input'
import {PhoneNumberUtil} from 'google-libphonenumber'
import {styles} from './styles'
import {useMemo} from 'react'
import {ShipToAddress, useShipToCreationContext} from '@hconnect/tuc-processes/shipToCreation'
import {parsePhoneNumber} from '@hconnect/tuc-processes/utils/phoneNumberUtils'
import {DEFAULT_MAP_ZOOM} from './constants'

const phoneNumberUtil = PhoneNumberUtil.getInstance()

const MapMarker: React.FC<{lat: number; lng: number}> = () => (
  <Box sx={styles.mapMarkerContainer} data-test-id="map-marker-container">
    <Box sx={styles.mapMarkerPin} data-test-id="map-marker-pin">
      <LocationOnIcon sx={styles.mapMarkerIcon} data-test-id="map-marker-icon" />
    </Box>
  </Box>
)

const MapDisplay: React.FC<{address: ShipToAddress; onEdit: () => void}> = ({address, onEdit}) => {
  const defaultProps = {
    center: {
      lat: address.latitude,
      lng: address.longitude
    },
    zoom: DEFAULT_MAP_ZOOM
  }

  return (
    <Card sx={styles.mapCard} data-test-id="map-display-card">
      <CardContent sx={styles.mapCardContent} data-test-id="map-card-content">
        <Box sx={styles.mapAddressContainer} data-test-id="map-address-container">
          <Typography variant="body1" sx={styles.mapAddressText} data-test-id="map-address-text">
            {address?.fullAddress}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={onEdit}
            startIcon={<EditIcon sx={styles.mapEditIcon} />}
            sx={styles.mapEditButton}
            data-test-id="map-edit-button"
          >
            {t('orderIntake.rmc.jobSiteModal.create.editAddressText', 'Edit')}
          </Button>
        </Box>
        <Box sx={styles.googleMapContainer} data-test-id="google-map-container">
          <GoogleMapReact
            bootstrapURLKeys={{key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_HUB || ''}}
            defaultCenter={defaultProps.center}
            defaultZoom={defaultProps.zoom}
            options={{
              disableDefaultUI: true,
              zoomControl: false,
              scrollwheel: false,
              draggable: false
            }}
            data-test-id="google-map-react"
          >
            <MapMarker lat={defaultProps.center.lat} lng={defaultProps.center.lng} />
          </GoogleMapReact>
        </Box>
      </CardContent>
    </Card>
  )
}

interface JobSiteCreationFormContentProps {
  onClose: () => void
  openMap: () => void
  submit: () => void
  isLoading: boolean
}

export const JobSiteCreationForm: React.FC<JobSiteCreationFormContentProps> = ({
  onClose,
  openMap,
  submit,
  isLoading
}) => {
  const {
    processData,
    shipToCreationForm: {watch, setValue, formState}
  } = useShipToCreationContext()

  const userProfile = useSelector<AppState, User | null>(
    (state) => getUserProfile(state).userProfile
  )

  const {errors} = formState

  const siteContactError = errors.siteContact
  const jobSiteAddressError = errors.jobSiteAddress
  const hasError = !!siteContactError || !!jobSiteAddressError

  const [jobSiteName, siteContact, jobSiteAddress] = watch([
    'jobSiteName',
    'siteContact',
    'jobSiteAddress'
  ])

  const setJobSiteName = (jobSiteName: string) => {
    setValue('jobSiteName', jobSiteName)
  }

  const setSiteContactName = (siteContactName: string) => {
    setValue('siteContact.name', siteContactName, {shouldValidate: true})
  }

  const onPhoneChange = (fullPhoneNumber: string, countryData?: CountryData) => {
    if (!fullPhoneNumber && siteContact?.phoneNumber?.number) {
      return
    }

    const phoneNumberObj = parsePhoneNumber(
      fullPhoneNumber,
      userProfile?.country ?? processData.country
    )
    const dialingCode = countryData?.dialCode ?? phoneNumberObj.dialingCode
    const country = countryData?.iso2 ?? phoneNumberObj.country

    const nationalNumber = (() => {
      try {
        const parsed = phoneNumberUtil.parse(
          fullPhoneNumber,
          userProfile?.country ?? processData.country
        )

        return parsed.getNationalNumber().toString()
      } catch (err) {
        const isOnlyDialingCode = dialingCode && fullPhoneNumber.length <= dialingCode.length + 1
        return isOnlyDialingCode ? '' : fullPhoneNumber.replace(`+${dialingCode}`, '').trim()
      }
    })()
    setValue(
      'siteContact.phoneNumber',
      {
        dialingCode,
        country,
        number: nationalNumber
      },
      {shouldValidate: true}
    )
  }

  const phoneNumberValue = useMemo(() => {
    if (!siteContact?.phoneNumber) return ''
    const dialingCode = siteContact.phoneNumber.dialingCode
    const cleanDialingCode = dialingCode?.startsWith('+') ? dialingCode.slice(1) : dialingCode
    return `+${cleanDialingCode}${siteContact.phoneNumber.number}`
  }, [siteContact?.phoneNumber])

  return (
    <>
      <DialogTitle sx={styles.dialogTitle} data-test-id="job-site-dialog-title">
        <Typography
          variant="h3"
          component="div"
          sx={styles.titleText}
          data-test-id="dialog-title-text"
        >
          {t('orderIntake.rmc.jobSiteModal.create.title')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
          data-test-id="dialog-close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent} data-test-id="job-site-dialog-content">
        <Box sx={styles.sectionContainer} data-test-id="job-site-address-section">
          <Typography
            variant="h3"
            component="div"
            sx={styles.sectionTitle}
            data-test-id="job-site-address-title"
          >
            {t('orderIntake.rmc.jobSiteModal.create.jobSiteAddressLabel')}
          </Typography>
        </Box>
        <Box sx={styles.sectionContainer} data-test-id="address-selection-section">
          {jobSiteAddress ? (
            <MapDisplay address={jobSiteAddress} onEdit={openMap} />
          ) : (
            <Button
              variant="outlined"
              fullWidth
              onClick={openMap}
              data-test-id="find-address-button"
              sx={styles.findAddressButton}
            >
              {t('orderIntake.rmc.jobSiteModal.create.findAddressButtonText')}
            </Button>
          )}
        </Box>
        <Box sx={styles.sectionContainer} data-test-id="job-site-name-section">
          <Typography
            variant="h3"
            component="div"
            sx={styles.sectionTitle}
            data-test-id="job-site-name-title"
          >
            {t('orderIntake.rmc.jobSiteModal.create.jobSiteNameLabel')}
          </Typography>
        </Box>
        <Box sx={styles.inputContainer} data-test-id="job-site-name-input-container">
          <TextField
            fullWidth
            variant="outlined"
            label={t(
              'orderIntake.rmc.jobSiteModal.create.jobSiteNameInputPlaceholder',
              'Job site name'
            )}
            placeholder={t('orderIntake.rmc.jobSiteModal.create.jobSiteNameInputPlaceholder')}
            value={jobSiteName ?? ''}
            onChange={(e) => setJobSiteName(e.target.value)}
            data-test-id="jobSite-name-input"
          />
        </Box>
        <Box sx={styles.sectionContainer} data-test-id="site-contact-section">
          <Typography
            variant="h3"
            component="div"
            sx={styles.sectionTitle}
            data-test-id="site-contact-title"
          >
            {t('orderIntake.rmc.jobSiteModal.create.siteContactLabel', 'Site contact')}
          </Typography>
        </Box>
        <Box sx={styles.inputContainer} data-test-id="site-contact-name-input-container">
          <TextField
            required
            error={!!siteContactError?.name}
            fullWidth
            variant="outlined"
            label={t('orderIntake.rmc.jobSiteModal.create.siteContactNameLabel', 'Contact name')}
            placeholder={t('orderIntake.rmc.jobSiteModal.create.siteContactNameInputPlaceholder')}
            value={siteContact?.name ?? ''}
            onChange={(e) => setSiteContactName(e.target.value)}
            helperText={siteContactError?.name?.message}
            data-test-id="site-contact-name-input"
          />
        </Box>

        <Box sx={styles.phoneContainer} data-test-id="phone-number-input-container">
          <PhoneNumberTextField
            required
            bordered
            id={'site-contact-phone-input'}
            fullWidth
            name={'site-contact-phone-input'}
            label={t('orderIntake.rmc.jobSiteModal.create.siteContactPhoneLabel', 'Phone number')}
            error={!!siteContactError?.phoneNumber}
            data-test-id={'site-contact-phone-input'}
            value={phoneNumberValue}
            onPhoneChange={(fullPhoneNumber, _, countryData) =>
              onPhoneChange(fullPhoneNumber, countryData)
            }
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            helperText={siteContactError?.phoneNumber?.message}
            options={{
              initialCountry:
                siteContact?.phoneNumber?.country ?? userProfile?.country ?? processData!.country,
              nationalMode: false,
              formatOnDisplay: false
            }}
            className="PhoneNumberTextField"
          />
        </Box>
        <Box sx={styles.sectionContainer} data-test-id="submit-button-section">
          <LoadingButton
            variant="outlined"
            fullWidth
            onClick={submit}
            loading={isLoading}
            data-test-id="add-jobSite-button"
            disabled={hasError}
            sx={styles.submitButton}
          >
            {t('orderIntake.rmc.jobSiteModal.create.sendButtonText')}
          </LoadingButton>
        </Box>
      </DialogContent>
    </>
  )
}
